/**
 * Platform administration page with tabbed interface for managing accounts,
 * modules, and platform administrators. Accessible only to super_admin users.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AccountsTab } from "./AccountsTab";
import { ActionsLibraryTab } from "./ActionsLibraryTab";
import { ModulesTab } from "./ModulesTab";
import { UsersTab } from "./UsersTab";

import { ActionTypeEditorForm } from "@/components/actions/ActionTypeEditorForm";
import { EditorDrawer } from "@/components/layout/EditorDrawer";
import { ModuleEditorForm } from "@/components/modules";
import { cn } from "@/lib/utils";

type AdminTab = "users" | "accounts" | "modules" | "actions";

const validTabs: AdminTab[] = ["users", "accounts", "modules", "actions"];

/**
 * Type guard to check if a string is a valid admin tab.
 * @param tab - the tab value to check
 */
function isValidTab(tab: string | null): tab is AdminTab {
	return tab !== null && validTabs.includes(tab as AdminTab);
}

/**
 * Super Admin Dashboard
 *
 * Central management interface for platform-wide configuration.
 * Only accessible to users with super_admin role.
 */
export function AdminPage() {
	const [searchParams] = useSearchParams();
	const tabParam = searchParams.get("tab");
	const initialTab = isValidTab(tabParam) ? tabParam : "users";
	const [activeTab, setActiveTab] = useState<AdminTab>(initialTab);

	// Read drawer state from URL params
	const drawer = searchParams.get("drawer"); // "module" | "action" | null
	const editId = searchParams.get("id"); // ID or null for create mode
	const isDrawerOpen = drawer === "module" || drawer === "action";

	const tabs: { id: AdminTab; label: string }[] = [
		{ id: "users", label: "Users" },
		{ id: "accounts", label: "Accounts" },
		{ id: "modules", label: "Modules" },
		{ id: "actions", label: "Actions Library" },
	];

	return (
		<div className="relative h-full overflow-hidden">
			{/* Scrollable content area */}
			<div className="h-full overflow-y-auto p-6">
				<header className="mb-6">
					<h1 className="text-2xl font-bold">Unios Setup</h1>
					<p className="text-muted-foreground">
						Platform-wide configuration and management
					</p>
				</header>

				{/* Tab Navigation */}
				<div className="border-b mb-6">
					<nav className="flex gap-4">
						{tabs.map((tab) => (
							<button
								key={tab.id}
								onClick={() => {
									setActiveTab(tab.id);
								}}
								className={cn(
									"pb-2 px-1 text-sm font-medium border-b-2 transition-colors",
									activeTab === tab.id
										? "border-primary text-foreground"
										: "border-transparent text-muted-foreground hover:text-foreground"
								)}
							>
								{tab.label}
							</button>
						))}
					</nav>
				</div>

				{/* Tab Content */}
				{activeTab === "users" && <UsersTab />}
				{activeTab === "accounts" && <AccountsTab />}
				{activeTab === "modules" && <ModulesTab />}
				{activeTab === "actions" && <ActionsLibraryTab />}
			</div>

			{/* Editor Drawer - slides over content area */}
			<EditorDrawer isOpen={isDrawerOpen}>
				{drawer === "module" && <ModuleEditorForm moduleId={editId} />}
				{drawer === "action" && <ActionTypeEditorForm actionId={editId} />}
			</EditorDrawer>
		</div>
	);
}
